/**
 * Offline backlog stats via OnlineSalesServer device_offline_status.
 *
 * POST {serverApi}deviceOfflineStatus/{StoreDB}/snapshot
 * Body: { pcName, type: 'INVOICE'|'TABLE', notSyncedCount, failedCount }
 *
 * GET {serverApi}deviceOfflineStatus/{StoreDB}/latestAll?type=INVOICE|TABLE
 *   → array of latest rows (all devices for the store)
 *
 * Triggers (callers):
 * - After each offline invoice sync attempt (debounced) — Record (POST)
 * - End User Shift / cashup — POST snapshot + GET latestAll; block if local pending
 *   OR any store device has notSyncedCount/failedCount > 0
 *
 * Soft-fails always on network/401/404: missing route or auth never throws to callers.
 * Auth: X-API-Key: development on these two calls only (not invoice/other APIs).
 */
import axios from 'axios';
import outbox from './outbox';
import voidOutbox from './voidOutbox';
import { Storage } from './Storage';
import connectivityMonitor from './connectivityMonitor';

const DEBOUNCE_MS = 3000;
const POST_TIMEOUT_MS = 10000;

/** Only for deviceOfflineStatus POST/GET — not used on invoice or other APIs. */
const DEVICE_OFFLINE_STATUS_HEADERS = {
    'X-API-Key': 'development'
};

/** @type {ReturnType<typeof setTimeout>|null} */
let invoiceDebounceTimer = null;
/** @type {ReturnType<typeof setTimeout>|null} */
let tableDebounceTimer = null;

/**
 * Count outbox rows into API buckets (matches OfflineInvoicesView stats).
 * notSynced = pending + syncing + sending
 * failed = failed + timeout + timed_out
 * @param {Array<{status?: string}>} items
 * @returns {{ notSyncedCount: number, failedCount: number }}
 */
export function countBacklogBuckets(items) {
    let notSyncedCount = 0;
    let failedCount = 0;
    const list = Array.isArray(items) ? items : [];
    for (const item of list) {
        const s = item?.status;
        if (s === 'pending' || s === 'syncing' || s === 'sending') {
            notSyncedCount += 1;
        } else if (s === 'failed' || s === 'timeout' || s === 'timed_out') {
            failedCount += 1;
        }
    }
    return { notSyncedCount, failedCount };
}

/**
 * @returns {string}
 */
function getServerApiBase() {
    if (typeof window === 'undefined') {
        return '';
    }
    return window.serverApi || '';
}

/**
 * Prefer connectivityMonitor; fall back to navigator.onLine so End User Shift
 * still hits the API when the monitor is stale but the drawer is usable.
 * @returns {boolean}
 */
function isLikelyOnline() {
    try {
        if (connectivityMonitor && typeof connectivityMonitor.getStatus === 'function') {
            if (connectivityMonitor.getStatus()) {
                return true;
            }
        }
    } catch (e) {
        /* ignore */
    }
    if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean') {
        return navigator.onLine;
    }
    return true;
}

/**
 * @returns {Promise<{ storeDB: string, pcName: string }|null>}
 */
async function resolveIdentity() {
    try {
        const store = await Storage.getDefaultStore();
        const settings = await Storage.getDeviceSettings();
        const storeDB = store?.StoreDB || store?.storeDB || '';
        const pcName =
            settings?.PC ||
            settings?.pc ||
            settings?.deviceName ||
            settings?.DeviceName ||
            '';
        if (!storeDB || !pcName) {
            console.warn(
                '[deviceOfflineStatus] skip: missing StoreDB or PC',
                { storeDB: storeDB || null, pcName: pcName || null, store, settings }
            );
            return null;
        }
        return { storeDB: String(storeDB), pcName: String(pcName) };
    } catch (e) {
        console.warn('[deviceOfflineStatus] identity resolve failed', e);
        return null;
    }
}

/**
 * @param {'INVOICE'|'TABLE'} type
 * @returns {Promise<{ notSyncedCount: number, failedCount: number }>}
 */
async function loadCounts(type) {
    if (type === 'TABLE') {
        const items = await voidOutbox.getAllVoidOutboxItems();
        return countBacklogBuckets(items);
    }
    const items = await outbox.getAllOutboxItems();
    return countBacklogBuckets(items);
}

/**
 * Shared preflight: base + identity. Logs skip reason. Never throws.
 * @returns {Promise<{ base: string, storeDB: string, pcName: string }|null>}
 */
async function resolveRequestContext() {
    const base = getServerApiBase();
    if (!base) {
        console.warn('[deviceOfflineStatus] skip: window.serverApi empty');
        return null;
    }
    if (!isLikelyOnline()) {
        console.warn('[deviceOfflineStatus] skip: offline (monitor + navigator)');
        return null;
    }
    const identity = await resolveIdentity();
    if (!identity) {
        return null;
    }
    return { base, storeDB: identity.storeDB, pcName: identity.pcName };
}

/**
 * POST snapshot once. Never throws to callers.
 * @param {'INVOICE'|'TABLE'} type
 * @returns {Promise<object|null>} server row or null
 */
export async function pushSnapshot(type = 'INVOICE') {
    try {
        const ctx = await resolveRequestContext();
        if (!ctx) {
            return null;
        }
        const { notSyncedCount, failedCount } = await loadCounts(type);
        const url = `${ctx.base}deviceOfflineStatus/${ctx.storeDB}/snapshot`;
        const body = {
            pcName: ctx.pcName,
            type,
            notSyncedCount,
            failedCount
        };
        console.log('[deviceOfflineStatus] POST', url, body);
        const { data } = await axios.post(url, body, {
            timeout: POST_TIMEOUT_MS,
            headers: DEVICE_OFFLINE_STATUS_HEADERS
        });
        console.log('[deviceOfflineStatus] snapshot ok', data);
        return data;
    } catch (e) {
        const status = e?.response?.status;
        const msg = e?.response?.data || e?.message || e;
        console.warn('[deviceOfflineStatus] snapshot failed (non-blocking)', status, msg);
        return null;
    }
}

/**
 * Debounced INVOICE snapshot after sync API attempts.
 */
export function scheduleInvoiceSnapshot() {
    if (invoiceDebounceTimer) {
        clearTimeout(invoiceDebounceTimer);
    }
    invoiceDebounceTimer = setTimeout(() => {
        invoiceDebounceTimer = null;
        pushSnapshot('INVOICE');
    }, DEBOUNCE_MS);
}

/**
 * Debounced TABLE snapshot after void sync attempts.
 */
export function scheduleTableSnapshot() {
    if (tableDebounceTimer) {
        clearTimeout(tableDebounceTimer);
    }
    tableDebounceTimer = setTimeout(() => {
        tableDebounceTimer = null;
        pushSnapshot('TABLE');
    }, DEBOUNCE_MS);
}

/**
 * Immediate INVOICE snapshot (cashup / shift end).
 * @returns {Promise<object|null>}
 */
export async function pushInvoiceSnapshotNow() {
    if (invoiceDebounceTimer) {
        clearTimeout(invoiceDebounceTimer);
        invoiceDebounceTimer = null;
    }
    return pushSnapshot('INVOICE');
}

/**
 * Server backlog units from a latest-snapshot row (notSynced + failed).
 * @param {object|null|undefined} row
 * @returns {number}
 */
export function serverBacklogFromSnapshot(row) {
    if (!row || typeof row !== 'object') {
        return 0;
    }
    const notSynced = Number(row.notSyncedCount);
    const failed = Number(row.failedCount);
    const a = Number.isFinite(notSynced) ? Math.max(0, notSynced) : 0;
    const b = Number.isFinite(failed) ? Math.max(0, failed) : 0;
    return a + b;
}

/**
 * From latestAll rows, pick this device's snapshot (case-insensitive pcName + type).
 * @param {unknown} rows
 * @param {string} pcName
 * @param {'INVOICE'|'TABLE'} type
 * @returns {object|null}
 */
export function pickDeviceRowFromLatestAll(rows, pcName, type = 'INVOICE') {
    if (!Array.isArray(rows) || !pcName) {
        return null;
    }
    const pc = String(pcName).trim().toLowerCase();
    const t = String(type).trim().toUpperCase();
    const match = rows.find((row) => {
        if (!row || typeof row !== 'object') {
            return false;
        }
        const rowPc = String(row.pcName || '').trim().toLowerCase();
        const rowType = String(row.type || 'INVOICE').trim().toUpperCase();
        return rowPc === pc && rowType === t;
    });
    return match || null;
}

/**
 * Sum notSynced + failed across all devices in latestAll (store-wide).
 * @param {unknown} rows
 * @param {'INVOICE'|'TABLE'} type
 * @returns {number}
 */
export function serverBacklogFromLatestAll(rows, type = 'INVOICE') {
    if (!Array.isArray(rows)) {
        return 0;
    }
    const t = String(type).trim().toUpperCase();
    let total = 0;
    for (const row of rows) {
        if (!row || typeof row !== 'object') {
            continue;
        }
        const rowType = String(row.type || 'INVOICE').trim().toUpperCase();
        if (rowType !== t) {
            continue;
        }
        total += serverBacklogFromSnapshot(row);
    }
    return total;
}

/**
 * Devices in latestAll with backlog &gt; 0 (for cashup messaging).
 * @param {unknown} rows
 * @param {'INVOICE'|'TABLE'} type
 * @returns {Array<{ pcName: string, notSyncedCount: number, failedCount: number, backlog: number }>}
 */
export function devicesWithBacklogFromLatestAll(rows, type = 'INVOICE') {
    if (!Array.isArray(rows)) {
        return [];
    }
    const t = String(type).trim().toUpperCase();
    const out = [];
    for (const row of rows) {
        if (!row || typeof row !== 'object') {
            continue;
        }
        const rowType = String(row.type || 'INVOICE').trim().toUpperCase();
        if (rowType !== t) {
            continue;
        }
        const backlog = serverBacklogFromSnapshot(row);
        if (backlog <= 0) {
            continue;
        }
        out.push({
            pcName: String(row.pcName || ''),
            notSyncedCount: Number(row.notSyncedCount) || 0,
            failedCount: Number(row.failedCount) || 0,
            backlog
        });
    }
    return out;
}

/**
 * GET latestAll offline-status snapshots for the store (all devices).
 * Never throws; returns [] on offline, 404, 401, or errors.
 * @param {'INVOICE'|'TABLE'} type
 * @returns {Promise<object[]>}
 */
export async function getLatestAllSnapshots(type = 'INVOICE') {
    try {
        const ctx = await resolveRequestContext();
        if (!ctx) {
            return [];
        }
        const url = `${ctx.base}deviceOfflineStatus/${ctx.storeDB}/latestAll`;
        console.log('[deviceOfflineStatus] GET', url, { type, pcName: ctx.pcName });
        const { data } = await axios.get(url, {
            params: { type },
            timeout: POST_TIMEOUT_MS,
            headers: DEVICE_OFFLINE_STATUS_HEADERS
        });
        const rows = Array.isArray(data) ? data : [];
        console.log('[deviceOfflineStatus] latestAll ok', type, {
            rowCount: rows.length,
            storeBacklog: serverBacklogFromLatestAll(rows, type),
            devicesWithBacklog: devicesWithBacklogFromLatestAll(rows, type)
        });
        return rows;
    } catch (e) {
        const status = e?.response?.status;
        const msg = e?.response?.data || e?.message || e;
        if (status === 404) {
            console.log('[deviceOfflineStatus] latestAll: no rows', type);
        } else {
            console.warn('[deviceOfflineStatus] latestAll failed (non-blocking)', status, msg);
        }
        return [];
    }
}

/**
 * GET latestAll; returns this device's row only (helpers / debug).
 * @param {'INVOICE'|'TABLE'} type
 * @returns {Promise<object|null>}
 */
export async function getLatestSnapshot(type = 'INVOICE') {
    try {
        const ctx = await resolveRequestContext();
        if (!ctx) {
            return null;
        }
        const rows = await getLatestAllSnapshots(type);
        return pickDeviceRowFromLatestAll(rows, ctx.pcName, type);
    } catch (e) {
        return null;
    }
}

/**
 * GET latestAll INVOICE snapshot for this device.
 * @returns {Promise<object|null>}
 */
export async function getLatestInvoiceSnapshot() {
    return getLatestSnapshot('INVOICE');
}

/**
 * End User Shift / cashup: keep local pending check + store-wide latestAll.
 * Block if this PC has unsent outbox OR any store device has notSynced/failed &gt; 0.
 * @returns {Promise<{
 *   pendingLocalCount: number,
 *   serverBacklogCount: number,
 *   shouldBlock: boolean,
 *   serverRows: object[],
 *   devicesWithBacklog: Array<object>,
 *   serverRow: object|null
 * }>}
 */
export async function runCashupOfflineStatusCheck() {
    const pendingLocalCount = await outbox.countPending().catch(() => 0);
    console.log('[deviceOfflineStatus] cashup check start', { pendingLocalCount });
    await pushInvoiceSnapshotNow().catch(() => null);
    const serverRows = await getLatestAllSnapshots('INVOICE').catch(() => []);
    const serverBacklogCount = serverBacklogFromLatestAll(serverRows, 'INVOICE');
    const devicesWithBacklog = devicesWithBacklogFromLatestAll(serverRows, 'INVOICE');
    const shouldBlock = pendingLocalCount > 0 || serverBacklogCount > 0;
    let serverRow = null;
    try {
        const ctx = await resolveRequestContext();
        if (ctx) {
            serverRow = pickDeviceRowFromLatestAll(serverRows, ctx.pcName, 'INVOICE');
        }
    } catch (e) {
        /* ignore */
    }
    console.log('[deviceOfflineStatus] cashup check done', {
        pendingLocalCount,
        serverBacklogCount,
        shouldBlock,
        devicesWithBacklog,
        serverRow
    });
    return {
        pendingLocalCount,
        serverBacklogCount,
        shouldBlock,
        serverRows,
        devicesWithBacklog,
        serverRow
    };
}

export default {
    countBacklogBuckets,
    serverBacklogFromSnapshot,
    serverBacklogFromLatestAll,
    devicesWithBacklogFromLatestAll,
    pickDeviceRowFromLatestAll,
    pushSnapshot,
    scheduleInvoiceSnapshot,
    scheduleTableSnapshot,
    pushInvoiceSnapshotNow,
    getLatestAllSnapshots,
    getLatestSnapshot,
    getLatestInvoiceSnapshot,
    runCashupOfflineStatusCheck
};
